import * as React from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { Typography, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { styled } from "@mui/system";
import { getNeonColor } from "../../theme";

function valuetext(value: number) {
    if (value > 0) {
        return `${new Date(value * 1000).toISOString().substring(14, 19)}`;
    } else {
        return `-${new Date(-value * 1000).toISOString().substring(14, 19)}`;
    }
}

// Neon styled slider component
const NeonSlider = styled(Slider)<{ glowColor?: string }>(({ theme, glowColor }) => {
    const neonColor = glowColor || getNeonColor(theme);

    return {
        color: neonColor,
        height: 6,
        padding: '10px 0',

        '& .MuiSlider-track': {
            border: 'none',
            background: `linear-gradient(90deg, ${neonColor}80 0%, ${neonColor} 100%)`,
            boxShadow: `0px 0px 8px ${neonColor}60, 0px 0px 16px ${neonColor}40`,
            transition: 'none',
        },

        '& .MuiSlider-rail': {
            opacity: 0.3,
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '3px',
        },

        '& .MuiSlider-thumb': {
            height: 18,
            width: 18,
            backgroundColor: '#1a1a2e',
            border: `2px solid ${neonColor}`,
            boxShadow: `0px 0px 10px ${neonColor}, 0px 0px 20px ${neonColor}60`,
            transition: 'none',
            marginTop: 0,
            marginLeft: 0,

            '&::before': {
                content: '""',
                position: 'absolute',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: neonColor,
                boxShadow: `0px 0px 6px ${neonColor}`,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
            },

            '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                boxShadow: `0px 0px 15px ${neonColor}, 0px 0px 30px ${neonColor}80`,
            },
        },

        '& .MuiSlider-valueLabel': {
            fontSize: '0.75rem',
            fontWeight: 600,
            fontFamily: 'monospace',
            padding: '4px 10px',
            borderRadius: '6px',
            backgroundColor: 'rgba(10, 10, 20, 0.95)',
            border: `1px solid ${neonColor}50`,
            boxShadow: `0px 0px 15px ${neonColor}40, 0px 4px 12px rgba(0,0,0,0.5)`,
            color: neonColor,
            letterSpacing: '0.5px',

            '&::before': {
                borderTopColor: 'rgba(10, 10, 20, 0.95)',
            },
        },

        '& .MuiSlider-mark': {
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            height: 10,
            width: 2,
            marginTop: -2,
            borderRadius: '1px',

            '&.MuiSlider-markActive': {
                backgroundColor: neonColor,
                boxShadow: `0px 0px 4px ${neonColor}`,
            },
        },

        '& .MuiSlider-markLabel': {
            fontSize: '0.7rem',
            color: 'rgba(255, 255, 255, 0.5)',
            fontWeight: 500,
            fontFamily: 'monospace',
            letterSpacing: '0.5px',
            marginTop: '4px',

            '&.MuiSlider-markLabelActive': {
                color: neonColor,
                textShadow: `0px 0px 6px ${neonColor}60`,
            },
        },
    };
});

// Neon container for sliders
const NeonSliderContainer = styled(Box)<{ glowColor?: string }>(({ theme, glowColor }) => {
    const neonColor = glowColor || getNeonColor(theme);

    return {
        position: 'relative',
        padding: '10px 20px 8px 20px',
        borderRadius: '10px',
        background: 'rgba(10, 10, 20, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'visible',

        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, transparent 0%, ${neonColor}60 50%, transparent 100%)`,
            boxShadow: `0px 0px 8px ${neonColor}40`,
            borderRadius: '10px 10px 0 0',
        },

        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '2px',
            background: neonColor,
            opacity: 0.6,
            boxShadow: `0px 0px 8px ${neonColor}, 0px 0px 16px ${neonColor}60`,
            borderRadius: '0 0 10px 10px',
        },
    };
});

// Neon label for sliders
const NeonSliderLabel = styled(Typography)<{ glowColor?: string }>(({ theme, glowColor }) => {
    const neonColor = glowColor || getNeonColor(theme);

    return {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: 'rgba(255, 255, 255, 0.7)',
        textTransform: 'uppercase',
        letterSpacing: '1.5px',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',

        '&::before': {
            content: '""',
            width: '3px',
            height: '12px',
            background: neonColor,
            borderRadius: '2px',
            boxShadow: `0px 0px 6px ${neonColor}`,
        },
    };
});

export default function TimestampInRoundSelect(props: {
    value: any;
    onChange: (nv: number | number[]) => void;
    freeze_time_end?: number;
    spike_time?: number;
    roundDuration: number;
    min?: number;
    valueLabelFormat?: (value: number) => string;
    sx?: any;
}) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const min = props.min ?? 0;
    const max = Math.max(props.roundDuration, min + 1);
    const raw = Number(props.value);
    const clamped = Number.isFinite(raw) ? Math.min(max, Math.max(min, raw)) : min;
    const formatValue = props.valueLabelFormat ?? valuetext;

    const marks = [];
    if (props.freeze_time_end !== undefined && props.freeze_time_end >= min && props.freeze_time_end <= max) {
        marks.push({ value: props.freeze_time_end, label: 'Start' });
    }
    if (props.spike_time !== undefined && props.spike_time >= min && props.spike_time <= max) {
        marks.push({ value: props.spike_time, label: 'Plant' });
    }

    return (
        <NeonSliderContainer sx={{ width: "100%", position: "relative", ...props.sx }} glowColor={neonColor}>
            <NeonSlider
                min={min}
                max={max}
                step={0.1}
                value={clamped}
                onChange={(event, value) => props.onChange(value)}
                valueLabelDisplay="auto"
                valueLabelFormat={formatValue}
                getAriaValueText={formatValue}
                marks={marks}
                glowColor={neonColor}
            />
        </NeonSliderContainer>
    );
}

export function TimestampRangeInRoundSelect(props: {
    value: number[];
    onChange: Dispatch<SetStateAction<number[]>>;
    roundDuration: number;
    sx?: any;
}) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    return (
        <NeonSliderContainer sx={{ width: "100%", ...props.sx }} glowColor={neonColor}>
            <NeonSlider
                min={-5}
                max={props.roundDuration}
                step={1}
                value={props.value}
                onChange={(event, value) => props.onChange(value as number[])}
                onChangeCommitted={(event, value) => props.onChange(value as number[])}
                valueLabelDisplay="auto"
                valueLabelFormat={valuetext}
                getAriaValueText={valuetext}
                glowColor={neonColor}
            />
        </NeonSliderContainer>
    );
}

export function TimestampRangeSelect(props: { value: any; onChange: any; label?: string }) {
    const [value, setValue] = useState(props.value);
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    const handleChange = (event: Event, newValue: number | number[]) => {
        setValue(newValue as number[]);
    };

    const marks = [
        { value: 5 * 60, label: valuetext(5 * 60) },
        { value: 10 * 60, label: valuetext(10 * 60) },
        { value: 15 * 60, label: valuetext(15 * 60) },
        { value: 20 * 60, label: valuetext(20 * 60) },
        { value: 25 * 60, label: valuetext(25 * 60) },
    ];

    return (
        <NeonSliderContainer sx={{ width: "320px" }} glowColor={neonColor}>
            <NeonSliderLabel glowColor={neonColor}>
                {props.label || "Timestamp"}
            </NeonSliderLabel>
            <NeonSlider
                getAriaLabel={() => "Timestamp range"}
                min={0}
                max={60 * 30}
                step={30}
                value={value}
                onChange={handleChange}
                onChangeCommitted={(event, value) => props.onChange(value)}
                valueLabelDisplay="auto"
                valueLabelFormat={valuetext}
                getAriaValueText={valuetext}
                marks={marks}
                glowColor={neonColor}
            />
        </NeonSliderContainer>
    );
}

export function Depth(props: { value: any; onChange: any; label?: string }) {
    const [value, setValue] = useState(props.value);
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    const handleChange = (event: Event, newValue: number | number[]) => {
        setValue(newValue as number[]);
    };

    const marks = [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
    ];

    return (
        <NeonSliderContainer sx={{ width: "320px" }} glowColor={neonColor}>
            <NeonSliderLabel glowColor={neonColor}>
                {props.label || "Depth"}
            </NeonSliderLabel>
            <NeonSlider
                getAriaLabel={() => "Depth"}
                min={1}
                max={6}
                step={1}
                value={value}
                onChange={handleChange}
                onChangeCommitted={props.onChange}
                valueLabelDisplay="auto"
                marks={marks}
                glowColor={neonColor}
            />
        </NeonSliderContainer>
    );
}

export function TimestampSelect(props: { value: number; onChange: any; label?: string; max: number }) {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    return (
        <NeonSliderContainer sx={{ width: "320px" }} glowColor={neonColor}>
            <NeonSliderLabel glowColor={neonColor}>
                {props.label || "Timestamp"}
            </NeonSliderLabel>
            <NeonSlider
                getAriaLabel={() => "Timestamp"}
                min={0}
                max={props.max}
                step={1}
                value={props.value}
                onChange={(event: Event, newValue: number | number[]) => props.onChange(newValue as number)}
                valueLabelDisplay="auto"
                valueLabelFormat={valuetext}
                getAriaValueText={valuetext}
                glowColor={neonColor}
            />
        </NeonSliderContainer>
    );
}
